import React, { createContext, useContext, useState, ReactNode, useEffect, useMemo, useCallback } from 'react';
import Cookies from 'js-cookie';
import { appData } from '../data/appData';
import { appDataFR } from '../data/appDataFR';
import { appDataPT } from '../data/appDataPT';
import { AppData, UITranslations, Category } from '../types';
import { loadLocalizedCatalog, type CatalogLang } from '../services/catalogService';

type Language = 'en' | 'fr' | 'pt';

const LANGUAGE_COOKIE_NAME = 'preferred_language';
const DEFAULT_LANGUAGE: Language = 'en';

export interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  appData: AppData;
  t: UITranslations;
}

const translations: Record<Language, UITranslations> = {
  en: appData.ui,
  fr: appDataFR.ui,
  pt: appDataPT.ui
};

const getAppData = (language: Language): AppData => {
  switch (language) {
    case 'fr':
      return appDataFR;
    case 'pt':
      return appDataPT;
    default:
      return appData;
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [liveCatalog, setLiveCatalog] = useState<Record<CatalogLang, Category[]> | null>(null);

  const refreshLiveCatalog = useCallback(async () => {
    try {
      const data = await loadLocalizedCatalog();
      if (data && data.en.length > 0) {
        setLiveCatalog(data);
      } else {
        setLiveCatalog({ en: [], pt: [], fr: [] });
      }
    } catch {
      setLiveCatalog({ en: [], pt: [], fr: [] });
    }
  }, []);

  // Initialize state with cookie value or default language
  const [language, setLanguageState] = useState<Language>(() => {
    const savedLanguage = Cookies.get(LANGUAGE_COOKIE_NAME) as Language;
    return savedLanguage || DEFAULT_LANGUAGE;
  });

  // Update cookie when language changes
  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    Cookies.set(LANGUAGE_COOKIE_NAME, newLanguage, {
      expires: 365, // Cookie expires in 1 year
      sameSite: 'strict',
      secure: window.location.protocol === 'https:',
      path: '/'
    });
  };

  // Effect to detect browser language on first visit (if no cookie exists)
  useEffect(() => {
    if (!Cookies.get(LANGUAGE_COOKIE_NAME)) {
      const browserLang = navigator.language.toLowerCase();
      if (browserLang.startsWith('pt')) {
        setLanguage('pt');
      } else if (browserLang.startsWith('fr')) {
        setLanguage('fr');
      } else {
        setLanguage('en');
      }
    }
  }, []);

  useEffect(() => {
    refreshLiveCatalog();
    window.addEventListener('catalog-updated', refreshLiveCatalog);
    return () => window.removeEventListener('catalog-updated', refreshLiveCatalog);
  }, [refreshLiveCatalog]);

  const staticAppData = getAppData(language);
  const currentTranslations = translations[language];

  const mergedAppData = useMemo((): AppData => {
    const langKey: CatalogLang = language === 'pt' ? 'pt' : language === 'fr' ? 'fr' : 'en';
    // Catalog comes only from Firestore (no fallback to bundled appData categories).
    const sourceCategories = liveCatalog === null ? [] : liveCatalog[langKey];

    const categories = sourceCategories.map((cat) => ({
      ...cat,
      items: cat.items.filter((item) => item.isVisible !== false)
    }));

    return {
      ...staticAppData,
      categories
    };
  }, [language, liveCatalog, staticAppData]);

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        appData: mergedAppData,
        t: currentTranslations
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext; 